import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Folder, Lock, Mail, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Notice } from '@/components/ui/misc'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { api, ApiError } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'
import { InviteButton, MemberRows } from './MemberList'
import type { PendingInvite, WorkspaceDetail, WorkspaceMember, WorkspaceSummary } from './types'

export function NewWorkspaceDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (workspace: WorkspaceSummary) => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (open) {
      setName('')
      setDescription('')
    }
  }, [open])

  const create = useMutation({
    mutationFn: () =>
      api.post<{ workspace: WorkspaceSummary }>('/workspaces', {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: ({ workspace }) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast.success(`${workspace.name} created`)
      onOpenChange(false)
      onCreated?.(workspace)
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New Project"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={name.trim().length < 2}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create project
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Project name"
          required
          htmlFor="workspace-name"
          hint="Choose a name your collaborators will recognise."
        >
          <Input
            id="workspace-name"
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Botanical Dye Research"
          />
        </Field>

        <Field label="Description (optional)" htmlFor="workspace-description">
          <Textarea
            id="workspace-description"
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What will your team be working on?"
          />
        </Field>

        <Notice title="Private to your team" icon={<Lock className="size-3.5" />}>
          Only invited members can access this project. You will be the owner and can invite
          people after creating it.
        </Notice>
      </div>
    </Dialog>
  )
}

export function InviteDialog({
  workspace,
  open,
  onOpenChange,
}: {
  workspace: { id: string; name: string }
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')

  useEffect(() => {
    if (open) setEmail('')
  }, [open])

  const invite = useMutation({
    mutationFn: () => api.post(`/workspaces/${workspace.id}/invites`, { email: email.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspace.id] })
      toast.success(`Invitation sent to ${email.trim()}`)
      onOpenChange(false)
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Invite to Project"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!email.includes('@')}
            loading={invite.isPending}
            onClick={() => invite.mutate()}
          >
            Send
          </Button>
        </>
      }
    >
      <p className="pb-4 text-sm text-ink-soft">
        Invite someone to join <strong>{workspace.name}</strong>. We will send them an invitation
        by email.
      </p>

      <Field label="Email address" htmlFor="invite-email">
        <Input
          id="invite-email"
          type="email"
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@example.com"
          onKeyDown={(event) => {
            if (event.key === 'Enter' && email.includes('@')) invite.mutate()
          }}
        />
      </Field>
    </Dialog>
  )
}

export function MembersDialog({
  workspace,
  members,
  open,
  onOpenChange,
  onInvite,
}: {
  workspace: WorkspaceSummary
  members: WorkspaceMember[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onInvite: () => void
}) {
  const queryClient = useQueryClient()
  const { user } = useCurrentUser()

  const remove = useMutation({
    mutationFn: (member: WorkspaceMember) =>
      api.delete(`/workspaces/${workspace.id}/members/${member.id}`),
    onSuccess: (_result, member) => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspace.id] })
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast.success(
        member.id === user?.id ? `You left ${workspace.name}` : `${member.name} was removed`,
      )
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  // Only an owner may read the invite list, and only an owner can act on it.
  const invites = useQuery({
    queryKey: ['workspace', workspace.id, 'invites'],
    queryFn: () => api.get<{ invites: PendingInvite[] }>(`/workspaces/${workspace.id}/invites`),
    enabled: open && workspace.isOwner,
  })

  const revoke = useMutation({
    mutationFn: (invite: PendingInvite) =>
      api.delete(`/workspaces/${workspace.id}/invites/${invite.id}`),
    onSuccess: (_result, invite) => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspace.id, 'invites'] })
      toast.success(`Invitation to ${invite.email} was withdrawn`)
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  const pending = invites.data?.invites ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={workspace.name} size="lg">
      <div className="flex items-center justify-between pb-2">
        <h3 className="font-display text-lg font-semibold text-ink">
          Members ({members.length})
        </h3>
        {workspace.isOwner && <InviteButton onClick={onInvite} />}
      </div>

      <MemberRows
        members={members}
        ownerId={workspace.ownerId}
        canManage={workspace.isOwner}
        currentUserId={user?.id}
        onRemove={(member) => remove.mutate(member)}
        removingId={remove.isPending ? (remove.variables?.id ?? null) : null}
      />

      {pending.length > 0 && (
        <section className="mt-6">
          <h3 className="pb-2 font-display text-lg font-semibold text-ink">
            Pending invitations ({pending.length})
          </h3>
          <ul className="divide-y divide-line rounded-[var(--radius-control)] border border-line">
            {pending.map((invite) => (
              <li key={invite.id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-dashed border-line text-ink-faint">
                  <Mail className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink">{invite.email}</p>
                  <p className="text-[12px] text-ink-muted">
                    Invited {formatDate(invite.createdAt)} · expires {formatDate(invite.expiresAt)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={revoke.isPending && revoke.variables?.id === invite.id}
                  onClick={() => revoke.mutate(invite)}
                >
                  Withdraw
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Dialog>
  )
}

/**
 * Picks which projects hold a record. The dialog submits the whole set
 * rather than individual adds, so unticking is as natural as ticking.
 */
export function SaveToWorkspaceDialog({
  recordId,
  open,
  onOpenChange,
}: {
  recordId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string[] | null>(null)

  const options = useQuery({
    queryKey: ['record-workspaces', recordId],
    queryFn: () =>
      api.get<{
        workspaces: { id: string; name: string; memberCount: number; contains: boolean; locked: boolean }[]
      }>(
        `/records/${recordId}/workspaces`,
      ),
    enabled: open && recordId !== null,
  })

  // Seeded from the server once per opening; after that the dialog owns the state.
  useEffect(() => {
    if (!open) setSelected(null)
    else if (selected === null && options.data) {
      setSelected(options.data.workspaces.filter((w) => w.contains).map((w) => w.id))
    }
  }, [open, options.data, selected])

  const save = useMutation({
    mutationFn: () =>
      api.put(`/records/${recordId}/workspaces`, { workspaceIds: selected ?? [] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['record-workspaces', recordId] })
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      queryClient.invalidateQueries({ queryKey: ['workspace'] })
      // Records in a project leave My Workspace, so its lists and counts change too.
      queryClient.invalidateQueries({ queryKey: ['records'] })
      toast.success(
        (selected ?? []).length > 0 ? 'Moved to your project' : 'Removed from projects, back in My Workspace',
      )
      onOpenChange(false)
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const all = options.data?.workspaces ?? []
    return needle ? all.filter((w) => w.name.toLowerCase().includes(needle)) : all
  }, [options.data, query])

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Save to Project"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={selected === null} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="relative pb-3">
        <Search className="pointer-events-none absolute left-3 top-[18px] size-4 -translate-y-1/2 text-ink-faint" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search projects"
          className="pl-9"
        />
      </div>

      {options.data && options.data.workspaces.length === 0 && (
        <p className="py-6 text-center text-sm text-ink-muted">
          You are not in any projects yet. Create one from the Projects tab.
        </p>
      )}

      <div className="space-y-2">
        {visible.map((workspace) => {
          const checked = selected?.includes(workspace.id) ?? false

          return (
            <button
              key={workspace.id}
              type="button"
              disabled={workspace.locked}
              onClick={() =>
                setSelected((current) =>
                  (current ?? []).includes(workspace.id)
                    ? (current ?? []).filter((id) => id !== workspace.id)
                    : [...(current ?? []), workspace.id],
                )
              }
              className={cn(
                'flex w-full items-center gap-3 rounded-[var(--radius-control)] border px-3 py-3 text-left transition-colors',
                checked ? 'border-brand bg-brand-soft/50' : 'border-line hover:bg-paper-muted',
                workspace.locked && 'cursor-not-allowed opacity-60 hover:bg-transparent',
              )}
            >
              <span className="flex size-9 items-center justify-center rounded-[var(--radius-control)] border border-line bg-paper">
                <Folder className={cn('size-4', checked ? 'text-brand' : 'text-ink-muted')} />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink">
                  {workspace.name}
                </span>
                <span className="block text-[12px] text-ink-muted">
                  {workspace.locked
                    ? 'Awaiting approval, records cannot change'
                    : `Project · ${workspace.memberCount} members`}
                </span>
              </span>

              <span
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-full border',
                  checked ? 'border-brand bg-brand text-white' : 'border-line-strong',
                )}
              >
                {checked && <Check className="size-3" />}
              </span>
            </button>
          )
        })}
      </div>
    </Dialog>
  )
}

export type { WorkspaceDetail }
